import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from '@shared/hooks/use-toast';
import { FileCheck2, Layers, ArrowRightLeft } from 'lucide-react';
import { statusUtil } from '@shared/utils/statusUtils';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { useCurrency } from '@shared/selectors/globalSelectors';
import { useBulkUpdateRate } from '../../../hooks';
import { useDateNormalization } from '@shared/hooks/useDateNormalization';
import { queryClient } from '@shared/api/queries/queryClient';

interface ReviewApplyProps {
  storageKey?: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  stopTracking?: () => void;
}

export const ReviewApply: React.FC<ReviewApplyProps> = ({
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
  stopTracking
}) => {
  const navigate = useNavigate();
  const dataLoadedRef = useRef(false);
  const [allStepsData, setAllStepsData] = useState<any[]>([]);
  const currency=useCurrency();
  const bulkUpdateRateMutation = useBulkUpdateRate();
  const { formatToUserDate } = useDateNormalization();

  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

  // Load all previous steps data for review
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const allData = [];
        for (let i = 0; i < currentStepIndex; i++) {
          try {
            allData.push(stepHelpers.getStepData(i));
          } catch (e) {
            allData.push(null);
          }
        }
        setAllStepsData(allData);
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  // Read cross-step data
  const step1Data = stepHelpers?.getStepData?.(0) || { selectedRecords: [] };
  const step2Data = stepHelpers?.getStepData?.(1) || { fieldsToEdit: {} };
  const step3Data = stepHelpers?.getStepData?.(2) || {};

  const selectedRecords = step1Data.selectedRecords || [];
  console.log('selected rates are',selectedRecords)
  const fieldsToEdit = step2Data.fieldsToEdit || {};
  const commonRateType = step2Data.commonRateType || null;
  const rateAdjustment = step3Data.rateAdjustment || null;

  // Count toggled fields
  const changedFieldNames = useMemo(() => {
    const names: string[] = [];
    if (fieldsToEdit.status) names.push("status");
    if (fieldsToEdit.category) names.push("category");
    if (fieldsToEdit.effectiveFrom) names.push("effective");
    if (fieldsToEdit.endDate) names.push("end");
    if (fieldsToEdit.rateStructure) names.push("rates");
    return names;
  }, [fieldsToEdit]);

  const hasRateChange = fieldsToEdit.rateStructure && rateAdjustment;

  // Compute after values for rate preview
  const computeAfter = (before: number): number => {
    if (!rateAdjustment) return before;
    const val = parseFloat(rateAdjustment.adjustmentValue) || 0;
    const sign = rateAdjustment.direction === 'decrease' ? -1 : 1;
    if (rateAdjustment.adjustmentType === 'percentage') {
      return before * (1 + (sign * val / 100));
    }
    return before + (sign * val);
  };

  const handleApply = async () => {
    if (clearValidationError) {
      clearValidationError();
    }

    try {
      const payload: any = {
        rate_ids: selectedRecords.map((r: any) => r.id)
      };

      if (fieldsToEdit.effectiveFrom && step3Data.effectiveFrom) {
        payload.effective_date = step3Data.effectiveFrom;
      }

      if (fieldsToEdit.endDate && step3Data.endDate) {
        payload.expiry_date = step3Data.endDate;
      }

      if (fieldsToEdit.rateStructure && step3Data.rateAdjustment) {
        payload.rate_adjustment = {
          direction: step3Data.rateAdjustment.direction,
          adjustment_type: step3Data.rateAdjustment.adjustmentType,
          value: Number(step3Data.rateAdjustment.adjustmentValue)
        };
      }

      await bulkUpdateRateMutation.mutateAsync(payload);

      toast({
        title: "Success",
        description: `Bulk edit successfully applied to ${selectedRecords.length} tariffs.`,
      });

        queryClient.refetchQueries({
          queryKey: ["onboarding", "rateList"],
        });

      if (stopTracking) {
        stopTracking();
      }

      if (stepHelpers && stepHelpers.resetAllData) {
        stepHelpers.resetAllData();
      }

      navigate('/plan-tarrif/plan-tarrif-landing?tab=tariff');
    } catch (error: any) {
      if (setValidationError) {
        setValidationError("Failed to apply bulk update.");
      }
    }
  };

  const fieldChangeColumns: TableColumn<any>[] = useMemo(() => {
    const columns: TableColumn<any>[] = [
     
      {
        key: "name",
        header: "Name",
        render: (val, record) => <span className="text-gray-800 font-medium">{record.rateName}</span>
      },
       {
        key: "code",
        header: "Version",
        render: (val, record) => <span className="font-mono text-xs font-medium text-gray-700">{record.rateCode}</span>
      },
    ];

    if (fieldsToEdit.status) {
      columns.push({
        key: "status",
        header: "Status",
        render: (val, record) => (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600 line-through decoration-red-300">{record.isActive ? "Active" : "Inactive"}</span>
            <span className="text-gray-400">→</span>
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-600">{step3Data.status || "Active"}</span>
          </div>
        )
      });
    }

    if (fieldsToEdit.category) {
      columns.push({
        key: "category",
        header: "Category",
        render: (val, record) => (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600 line-through decoration-red-300">{record.utilityService}</span>
            <span className="text-gray-400">→</span>
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-600">{step3Data.category || "Residential"}</span>
          </div>
        )
      });
    }

    if (fieldsToEdit.effectiveFrom) {
      columns.push({
        key: "effectiveFrom",
        header: "Effective From",
        render: (val, record) => (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600 line-through decoration-red-300">{record.effectiveDate ? formatToUserDate(record.effectiveDate) : "—"}</span>
            <span className="text-gray-400">→</span>
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-600">{step3Data.effectiveFrom ? formatToUserDate(step3Data.effectiveFrom) : "—"}</span>
          </div>
        )
      });
    }

    if (fieldsToEdit.endDate) {
      columns.push({
        key: "endDate",
        header: "Effective To",
        render: (val, record) => (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600 line-through decoration-red-300">{record.expiryDate ? formatToUserDate(record.expiryDate) : "Ongoing"}</span>
            <span className="text-gray-400">→</span>
            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-emerald-50 text-emerald-600">{step3Data.endDate ? formatToUserDate(step3Data.endDate) : "—"}</span>
          </div>
        )
      });
    }

    return columns;
  }, [fieldsToEdit, step3Data]);

  const rateChangeColumns: TableColumn<any>[] = useMemo(() => {
    return [
      {
        key: "name",
        header: "Name",
        render: (val, record) => <span className="text-gray-800 font-medium">{record.rateName}</span>
      },
      {
        key: "code",
        header: "Code",
        render: (val, record) => <span className="font-mono text-xs font-medium text-gray-700">{record.rateCode}</span>
      },
      {
        key: "component",
        header: "Rate Component",
        render: (val, record) => {
          const rawRates = record.rate;
          const rates = Array.isArray(rawRates)
            ? rawRates
            : rawRates && typeof rawRates === "object"
            ? [rawRates]
            : typeof rawRates === "number"
            ? [{ rate: rawRates }]
            : [];
          if (!rates.length) return <span className="text-gray-600">{record.rateTypeDisplay || "Rate"}</span>;
          
          return (
            <div className="flex flex-col gap-2 my-1">
              {rates.map((r: any, idx: number) => {
                const subName =
                  r.name ||
                  r.slab ||
                  r.seasonName ||
                  r.mode ||
                  r.type ||
                  (r.fixed !== undefined ? "Fixed Rate" : "") ||
                  (r.rate !== undefined ? "Base Rate" : "") ||
                  record.rateTypeDisplay ||
                  "Base Rate";
                return (
                  <div key={idx} className="flex items-center h-7">
                    <span className="text-sm text-gray-600">{subName}</span>
                  </div>
                );
              })}
            </div>
          );
        }
      },
      {
        key: "before",
        header: "Before",
        render: (val, record) => {
          const rawRates = record.rate;
          const rates = Array.isArray(rawRates)
            ? rawRates
            : rawRates && typeof rawRates === "object"
            ? [rawRates]
            : typeof rawRates === "number"
            ? [{ rate: rawRates }]
            : [];
          if (!rates.length) return <span>-</span>;
          
          return (
            <div className="flex flex-col gap-2 my-1">
              {rates.map((r: any, idx: number) => {
                const before =
                  r.rate ??
                  r.fixed ??
                  r.fixedRate ??
                  r.unitSeasonalRate ??
                  r.baseRate ??
                  r.rateAmount ??
                  (typeof r === "number" ? r : 0);
                return (
                  <div key={idx} className="flex items-center h-7">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-medium bg-red-50 text-red-600 line-through decoration-red-300">
                      {currency} {parseFloat(Number(before).toFixed(4))}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        }
      },
      {
        key: "after",
        header: "After",
        render: (val, record) => {
          const rawRates = record.rate;
          const rates = Array.isArray(rawRates)
            ? rawRates
            : rawRates && typeof rawRates === "object"
            ? [rawRates]
            : typeof rawRates === "number"
            ? [{ rate: rawRates }]
            : [];
          if (!rates.length) return <span>-</span>;
          
          return (
            <div className="flex flex-col gap-2 my-1">
              {rates.map((r: any, idx: number) => {
                const before =
                  r.rate ??
                  r.fixed ??
                  r.fixedRate ??
                  r.unitSeasonalRate ??
                  r.baseRate ??
                  r.rateAmount ??
                  (typeof r === "number" ? r : 0);
                const after = computeAfter(before);
                return (
                  <div key={idx} className="flex items-center h-7">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-mono font-medium bg-emerald-50 text-emerald-600">
                      {currency} {parseFloat(Number(after).toFixed(4))}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        }
      }
    ];
  }, [rateAdjustment, currency]);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <h3 className="text-xl font-semibold text-gray-800">Review & Apply</h3>
        <p className="text-gray-500 mt-1">
          Confirm the changes below. They will be applied to <strong>{selectedRecords.length}</strong> tariffs when you click <strong>Apply</strong>.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-start gap-4">
          <div>
            <p className="text-xs font-medium text-gray-400 tracking-wider">Records</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{selectedRecords.length}</p>
            <p className="text-sm text-gray-500">tariffs selected</p>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-start gap-4">
          <div>
            <p className="text-xs font-medium text-gray-400 tracking-wider">Fields Changed</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{changedFieldNames.length}</p>
            <p className="text-sm text-gray-500">{changedFieldNames.join(", ")}</p>
          </div>
        </div>

        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex items-start gap-4">
          <div>
            <p className="text-xs font-medium text-gray-400 tracking-wider">Structural Changes</p>
            <p className="text-2xl font-bold text-gray-900 mt-0.5">{hasRateChange ? 1 : 0}</p>
            <p className="text-sm text-gray-500">{hasRateChange ? "rates" : "No structural changes"}</p>
          </div>
        </div>
      </div>

      {/* Field Changes Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h4 className="text-base font-semibold text-gray-800">Field Changes</h4>
        </div>
        <GenericTable
          data={selectedRecords}
          columns={fieldChangeColumns}
          rowKey="id"
        />
      </div>

      {/* Rate Change Preview */}
      {hasRateChange && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100">
            <h4 className="text-base font-semibold text-gray-800">Rate Change Preview</h4>
            <p className="text-xs text-gray-500 mt-1">
              {rateAdjustment.direction === 'increase' ? 'Increasing' : 'Decreasing'} by{' '}
              {rateAdjustment.adjustmentType === 'percentage'
                ? `${rateAdjustment.adjustmentValue}%`
                : `$${rateAdjustment.adjustmentValue}`}
            </p>
          </div>
          <GenericTable
            data={selectedRecords}
            columns={rateChangeColumns}
            rowKey="id"
          />
        </div>
      )}

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isValidating}
        >
          Previous
        </Button>
        <Button
          onClick={handleApply}
          disabled={isValidating}
          isLoading={bulkUpdateRateMutation.isPending}
          className="min-w-[120px]"
        >
          Apply Changes
        </Button>
      </div>
    </div>
  );
};
